'use client';

import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Skeleton, Stack, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined';
import PhoneAndroidOutlinedIcon from '@mui/icons-material/PhoneAndroidOutlined';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { getCommissionWallet } from '../../redux/buzzbridge/actions';
import { commissionWalletSelector, gettingCommissionWalletSelector } from '../../redux/buzzbridge/selectors';
import { buildCommissionEnrollmentRows, formatCommissionUsdCents } from '../../utils/commissionWallet';
import { CommissionActivityModal } from './CommissionActivityModal';

const GOLD = '#ffc965';
const MINT = '#00fdc1';
const SURFACE_CONTAINER = '#1a1919';
const ON_VARIANT = '#adaaaa';
const RADIUS_FULL = '0.75rem';

export interface CreatorCommissionEarningsSectionProps {
    onOpenCampaign?: (campaignId: string, participantId?: string) => void;
    /** When true, parent supplies the section title (e.g. segmented dashboard tabs). */
    embedded?: boolean;
}

export function CreatorCommissionEarningsSection({
    onOpenCampaign,
    embedded = false,
}: CreatorCommissionEarningsSectionProps) {
    const dispatch = useDispatch();
    const wallet = useSelector(commissionWalletSelector);
    const loading = useSelector(gettingCommissionWalletSelector);
    const [copiedKey, setCopiedKey] = useState<string | null>(null);
    const [activityTarget, setActivityTarget] = useState<{
        enrollmentId?: string;
        programId?: string;
        campaignId?: string;
    } | null>(null);

    useEffect(() => {
        dispatch(getCommissionWallet());
    }, [dispatch]);

    const rows = useMemo(() => buildCommissionEnrollmentRows(wallet ?? undefined), [wallet]);
    const pending = wallet?.commission?.pending_cents ?? 0;
    const available = wallet?.commission?.balance_cents ?? 0;
    const lifetimeCleared = wallet?.commission?.lifetime_cleared_cents ?? 0;

    const handleCopyLink = async (key: string, url: string | null) => {
        if (!url) return;
        try {
            await navigator.clipboard.writeText(url);
            setCopiedKey(key);
            setTimeout(() => setCopiedKey(null), 2000);
        } catch {
            /** ignore */
        }
    };

    const openActivity = (row?: { enrollmentId?: string; programId?: string; campaignId?: string }) => {
        if (row?.enrollmentId || row?.programId || row?.campaignId) {
            setActivityTarget(row);
            return;
        }
        const first = rows[0];
        if (first) {
            setActivityTarget({
                enrollmentId: first.enrollmentId,
                programId: first.programId,
                campaignId: first.campaignId,
            });
        }
    };

    if (!loading && !wallet) {
        return null;
    }

    if (!loading && rows.length === 0 && pending === 0 && available === 0 && lifetimeCleared === 0) {
        return null;
    }

    return (
        <Box sx={{ width: '100%', mt: embedded ? 0 : { xs: 2, sm: 3 } }}>
            {!embedded ? (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'stretch', sm: 'flex-end' },
                        gap: 1.5,
                        mb: 2,
                    }}>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: { xs: 22, sm: 26 },
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                        }}>
                        {rows.every(r => r.isAppInstall)
                            ? 'App install campaigns'
                            : rows.some(r => r.isAppInstall)
                            ? 'Performance campaigns'
                            : 'Commission campaigns'}
                        {rows.some(r => r.isAppInstall) ? (
                            <PhoneAndroidOutlinedIcon sx={{ color: '#ffc965', fontSize: 22 }} />
                        ) : (
                            <PercentOutlinedIcon sx={{ color: GOLD, fontSize: 22 }} />
                        )}
                    </Typography>
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 12, color: ON_VARIANT }}>
                        Paid after campaign ends
                    </Typography>
                </Box>
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'stretch', sm: 'flex-end' },
                        gap: 1,
                        mb: 2,
                    }}>
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 12, color: ON_VARIANT }}>
                        Paid after campaign ends
                    </Typography>
                </Box>
            )}

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr' },
                    gap: 1.5,
                    mb: 2,
                }}>
                <Box
                    sx={{
                        bgcolor: SURFACE_CONTAINER,
                        borderRadius: 2,
                        p: 2,
                        border: '1px solid rgba(255,255,255,0.05)',
                    }}>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 10,
                            letterSpacing: '0.15em',
                            textTransform: 'uppercase',
                            color: ON_VARIANT,
                            mb: 0.5,
                        }}>
                        Pending
                    </Typography>
                    {loading && !wallet ? (
                        <Skeleton variant="text" width={72} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
                    ) : (
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 22, color: GOLD }}>
                            {formatCommissionUsdCents(pending)}
                        </Typography>
                    )}
                </Box>
                <Box
                    sx={{
                        bgcolor: SURFACE_CONTAINER,
                        borderRadius: 2,
                        p: 2,
                        border: '1px solid rgba(255,255,255,0.05)',
                    }}>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 10,
                            letterSpacing: '0.15em',
                            textTransform: 'uppercase',
                            color: ON_VARIANT,
                            mb: 0.5,
                        }}>
                        Cleared (ready)
                    </Typography>
                    {loading && !wallet ? (
                        <Skeleton variant="text" width={72} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
                    ) : (
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 22, color: MINT }}>
                            {formatCommissionUsdCents(available)}
                        </Typography>
                    )}
                </Box>
            </Box>

            {rows.length > 0 && rows.every(r => !r.isAppInstall) ? (
                <Button
                    fullWidth
                    onClick={() => openActivity()}
                    startIcon={<HistoryOutlinedIcon sx={{ fontSize: 18 }} />}
                    sx={{
                        mb: 2,
                        py: 1.5,
                        borderRadius: RADIUS_FULL,
                        textTransform: 'uppercase',
                        letterSpacing: '0.12em',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 12,
                        border: `1px solid rgba(255, 201, 101, 0.25)`,
                        bgcolor: SURFACE_CONTAINER,
                        color: GOLD,
                        '&:hover': { bgcolor: '#222', borderColor: `${GOLD}55` },
                    }}>
                    View activity
                </Button>
            ) : null}

            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 11, color: ON_VARIANT, mb: 1.5 }}>
                Cleared commission moves into your campaign balance — use Cash Out in Quick Actions to withdraw. Pending
                amounts clear after the campaign ends.
            </Typography>

            <Stack spacing={1.5}>
                {loading && !wallet ? (
                    <>
                        <Skeleton
                            variant="rounded"
                            height={88}
                            sx={{ bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 2 }}
                        />
                        <Skeleton
                            variant="rounded"
                            height={88}
                            sx={{ bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 2 }}
                        />
                    </>
                ) : rows.length === 0 ? (
                    <Typography sx={{ color: ON_VARIANT, fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 14 }}>
                        No approved commission campaigns yet.
                    </Typography>
                ) : (
                    rows.map(row => {
                        const clickable = Boolean(row.campaignId && onOpenCampaign);
                        const copyKey = row.shareUrl || row.code;
                        const accentColor = row.isAppInstall ? '#ffc965' : MINT;
                        const cpaCentsLabel =
                            row.cpaCents != null
                                ? `~${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
                                      row.cpaCents / 100,
                                  )} per ${row.kind === 'web_visit' ? 'visit' : 'install'}`
                                : row.kind === 'web_visit'
                                ? 'Per visit'
                                : 'Per install';
                        return (
                            <Box
                                key={row.enrollmentId}
                                onClick={() => {
                                    if (clickable && row.campaignId) {
                                        onOpenCampaign?.(row.campaignId, row.participantId);
                                    }
                                }}
                                sx={{
                                    p: { xs: 2, sm: 2.5 },
                                    borderRadius: 2,
                                    bgcolor: '#131313',
                                    borderLeft: `4px solid ${accentColor}`,
                                    cursor: clickable ? 'pointer' : 'default',
                                    transition: 'transform 0.2s',
                                    '@media (min-width: 600px)': {
                                        '&:hover': clickable ? { transform: 'translateX(4px)' } : {},
                                    },
                                }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        justifyContent: 'space-between',
                                        gap: 1.5,
                                    }}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                                            {row.isAppInstall && (
                                                <PhoneAndroidOutlinedIcon sx={{ fontSize: 14, color: accentColor }} />
                                            )}
                                            <Typography
                                                sx={{
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    fontSize: { xs: 15, sm: 17 },
                                                    color: '#fff',
                                                    wordBreak: 'break-word',
                                                }}>
                                                {row.title}
                                            </Typography>
                                        </Box>
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                fontSize: 12,
                                                color: ON_VARIANT,
                                                mt: 0.5,
                                            }}>
                                            {row.isAppInstall
                                                ? cpaCentsLabel
                                                : row.rate != null
                                                ? `${row.rate}% commission`
                                                : 'Commission'}
                                            {!row.isAppInstall && row.kind ? ` · ${row.kind}` : ''}
                                            {row.programStatus ? ` · ${row.programStatus}` : ''}
                                        </Typography>
                                        {row.code ? (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.75,
                                                    mt: 1,
                                                    flexWrap: 'wrap',
                                                }}>
                                                <Typography
                                                    sx={{
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                        fontSize: 12,
                                                        color: GOLD,
                                                        letterSpacing: '0.06em',
                                                    }}>
                                                    {row.code}
                                                </Typography>
                                                {row.shareUrl ? (
                                                    <Button
                                                        size="small"
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            void handleCopyLink(copyKey, row.shareUrl);
                                                        }}
                                                        startIcon={
                                                            copiedKey === copyKey ? (
                                                                <CheckIcon sx={{ fontSize: 14 }} />
                                                            ) : (
                                                                <ContentCopyIcon sx={{ fontSize: 14 }} />
                                                            )
                                                        }
                                                        sx={{
                                                            minWidth: 0,
                                                            px: 1,
                                                            py: 0.25,
                                                            color: ON_VARIANT,
                                                            textTransform: 'none',
                                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                                            fontSize: 11,
                                                            '&:hover': {
                                                                color: row.isAppInstall ? accentColor : GOLD,
                                                                bgcolor: 'transparent',
                                                            },
                                                        }}
                                                        aria-label={`Copy ${
                                                            row.isAppInstall ? 'referral' : 'promo'
                                                        } link for ${row.code}`}>
                                                        {copiedKey === copyKey
                                                            ? 'Copied'
                                                            : row.isAppInstall
                                                            ? 'Copy referral link'
                                                            : 'Copy link'}
                                                    </Button>
                                                ) : null}
                                                <Button
                                                    size="small"
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        openActivity({
                                                            enrollmentId: row.enrollmentId,
                                                            programId: row.programId,
                                                            campaignId: row.campaignId,
                                                        });
                                                    }}
                                                    sx={{
                                                        minWidth: 0,
                                                        px: 1,
                                                        py: 0.25,
                                                        color: ON_VARIANT,
                                                        textTransform: 'none',
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                        fontSize: 11,
                                                        '&:hover': { color: GOLD, bgcolor: 'transparent' },
                                                    }}>
                                                    View activity
                                                </Button>
                                            </Box>
                                        ) : null}
                                    </Box>
                                    <Box
                                        sx={{
                                            textAlign: { xs: 'left', sm: 'right' },
                                            flexShrink: 0,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 0.5,
                                        }}>
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                fontSize: 18,
                                                color: '#fff',
                                            }}>
                                            {formatCommissionUsdCents(row.totalEarnedCents)}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                fontSize: 11,
                                                color: ON_VARIANT,
                                            }}>
                                            {formatCommissionUsdCents(row.pendingCents)} pending
                                            {' · '}
                                            {formatCommissionUsdCents(row.clearedCents)} cleared
                                        </Typography>
                                        <Box
                                            sx={{
                                                alignSelf: { xs: 'flex-start', sm: 'flex-end' },
                                                mt: 0.5,
                                                px: 1.25,
                                                py: 0.35,
                                                borderRadius: RADIUS_FULL,
                                                bgcolor: row.isAppInstall
                                                    ? 'rgba(255,201,101,0.12)'
                                                    : 'rgba(0, 253, 193, 0.12)',
                                                color: accentColor,
                                                fontSize: 10,
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                letterSpacing: '0.1em',
                                                textTransform: 'uppercase',
                                            }}>
                                            {row.isAppInstall
                                                ? row.kind === 'web_visit'
                                                    ? 'Web Visit'
                                                    : 'App Install'
                                                : 'Earned'}
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })
                )}
            </Stack>

            <CommissionActivityModal
                open={Boolean(activityTarget)}
                onClose={() => setActivityTarget(null)}
                enrollmentId={activityTarget?.enrollmentId}
                programId={activityTarget?.programId}
                campaignId={activityTarget?.campaignId}
            />
        </Box>
    );
}
